import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getLocalItem } from '../../utils/storage';

// Mechanic "profile basics" — a one-time blocking form shown to EVERY mechanic on
// the dashboard: (1) language, (2) uniform T-shirt size. One screen, one Save.
//
// Server-driven: mechanic-dashboard.jsx mounts it only when
// GET /api/mechanic_wizard_config says profile_basics_complete === false. If that
// fetch fails, or the backend fails open, this is never mounted — no form, no trap.
// It replaces the old standalone language modal (the language is saved through the
// same call), so a mechanic never sees two forms back to back.
//
// NEVER blocks a mechanic. A small "Later" link is ALWAYS available (it is a busy
// weekend — a mechanic mid-job must be able to reach the dashboard in one tap). It
// closes the form for this session only; the server still says incomplete, so it
// comes back on the next login until both answers are saved. Save is the clear
// primary action, so most mechanics answer the two questions the first time.
//
// Stacking: below the training modal (100000) and the booking decision gate
// (100001) — a timed booking offer must always outrank this form. It does NOT lock
// body scroll: the decision gate saves/restores body overflow and two lockers
// interleaving could leave the page stuck.
//
// SSR: nothing here touches window/document at module load or during a server
// render. It renders null on the server and until componentDidMount.

const API = 'https://ridenrepair.com';
const SAVE_TIMEOUT_MS = 15000;
const Z_INDEX = 99000;

const LANGS = [
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'mr', label: 'मराठी' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'en', label: 'English' },
];

const SIZES = ['S', 'M', 'L', 'XL', 'XXL', '3XL'];

// Shown until a language is picked: Hindi + English together.
const STR_DEFAULT = {
  title: 'अपनी जानकारी दें · Your details',
  sub: 'सिर्फ़ एक बार · One time only',
  langQ: 'भाषा चुनें · Language',
  langHint: 'कॉल और अपडेट इसी भाषा में आएँगे',
  sizeQ: 'टी-शर्ट साइज़ · T-shirt size',
  sizeHint: 'आपकी Ride N Repair यूनिफ़ॉर्म टी-शर्ट के लिए',
  save: 'सेव करें · Save',
  saving: 'सेव हो रहा है…',
  error: 'सेव नहीं हुआ। इंटरनेट देखें और दोबारा कोशिश करें।',
  retry: 'दोबारा कोशिश करें · Try again',
  later: 'बाद में · Later',
};

const STR = {
  hi: {
    title: 'अपनी जानकारी दें',
    sub: 'सिर्फ़ एक बार — 2 सवाल',
    langQ: 'आपकी भाषा',
    langHint: 'कॉल और अपडेट इसी भाषा में आएँगे',
    sizeQ: 'आपका टी-शर्ट साइज़',
    sizeHint: 'आपकी Ride N Repair यूनिफ़ॉर्म टी-शर्ट के लिए',
    save: 'सेव करें',
    saving: 'सेव हो रहा है…',
    error: 'सेव नहीं हुआ। इंटरनेट देखें और दोबारा कोशिश करें।',
    retry: 'दोबारा कोशिश करें',
    later: 'बाद में',
  },
  ta: {
    title: 'உங்கள் விவரங்கள்',
    sub: 'ஒரே ஒரு முறை — 2 கேள்விகள்',
    langQ: 'உங்கள் மொழி',
    langHint: 'அழைப்புகள் இந்த மொழியில் வரும்',
    sizeQ: 'உங்கள் டி-ஷர்ட் அளவு',
    sizeHint: 'Ride N Repair யூனிஃபார்ம் டி-ஷர்ட்டுக்காக',
    save: 'சேமிக்கவும்',
    saving: 'சேமிக்கிறது…',
    error: 'சேமிக்க முடியவில்லை. இணையத்தைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.',
    retry: 'மீண்டும் முயற்சிக்கவும்',
    later: 'பிறகு',
  },
  te: {
    title: 'మీ వివరాలు',
    sub: 'ఒక్కసారి మాత్రమే — 2 ప్రశ్నలు',
    langQ: 'మీ భాష',
    langHint: 'కాల్స్ ఈ భాషలోనే వస్తాయి',
    sizeQ: 'మీ టీ-షర్ట్ సైజు',
    sizeHint: 'Ride N Repair యూనిఫాం టీ-షర్ట్ కోసం',
    save: 'సేవ్ చేయండి',
    saving: 'సేవ్ అవుతోంది…',
    error: 'సేవ్ కాలేదు. ఇంటర్నెట్ చూసి మళ్లీ ప్రయత్నించండి.',
    retry: 'మళ్లీ ప్రయత్నించండి',
    later: 'తర్వాత',
  },
  mr: {
    title: 'तुमची माहिती द्या',
    sub: 'फक्त एकदाच — २ प्रश्न',
    langQ: 'तुमची भाषा',
    langHint: 'कॉल आणि अपडेट याच भाषेत येतील',
    sizeQ: 'तुमचा टी-शर्ट साइज',
    sizeHint: 'तुमच्या Ride N Repair युनिफॉर्म टी-शर्टसाठी',
    save: 'सेव्ह करा',
    saving: 'सेव्ह होत आहे…',
    error: 'सेव्ह झाले नाही. इंटरनेट तपासा आणि पुन्हा प्रयत्न करा.',
    retry: 'पुन्हा प्रयत्न करा',
    later: 'नंतर',
  },
  bn: {
    title: 'আপনার তথ্য দিন',
    sub: 'শুধু একবার — ২টি প্রশ্ন',
    langQ: 'আপনার ভাষা',
    langHint: 'কল ও আপডেট এই ভাষাতেই আসবে',
    sizeQ: 'আপনার টি-শার্টের সাইজ',
    sizeHint: 'আপনার Ride N Repair ইউনিফর্ম টি-শার্টের জন্য',
    save: 'সেভ করুন',
    saving: 'সেভ হচ্ছে…',
    error: 'সেভ হয়নি। ইন্টারনেট দেখে আবার চেষ্টা করুন।',
    retry: 'আবার চেষ্টা করুন',
    later: 'পরে',
  },
  kn: {
    title: 'ನಿಮ್ಮ ವಿವರಗಳು',
    sub: 'ಒಂದೇ ಬಾರಿ — 2 ಪ್ರಶ್ನೆಗಳು',
    langQ: 'ನಿಮ್ಮ ಭಾಷೆ',
    langHint: 'ಕರೆಗಳು ಇದೇ ಭಾಷೆಯಲ್ಲಿ ಬರುತ್ತವೆ',
    sizeQ: 'ನಿಮ್ಮ ಟಿ-ಶರ್ಟ್ ಸೈಜ್',
    sizeHint: 'Ride N Repair ಯೂನಿಫಾರ್ಮ್ ಟಿ-ಶರ್ಟ್‌ಗಾಗಿ',
    save: 'ಸೇವ್ ಮಾಡಿ',
    saving: 'ಸೇವ್ ಆಗುತ್ತಿದೆ…',
    error: 'ಸೇವ್ ಆಗಲಿಲ್ಲ. ಇಂಟರ್ನೆಟ್ ನೋಡಿ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    retry: 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
    later: 'ನಂತರ',
  },
  en: {
    title: 'Your details',
    sub: 'One time only — 2 questions',
    langQ: 'Your language',
    langHint: 'Calls and updates will come in this language',
    sizeQ: 'Your T-shirt size',
    sizeHint: 'For your Ride N Repair uniform T-shirt',
    save: 'Save',
    saving: 'Saving…',
    error: 'Could not save. Check your internet and try again.',
    retry: 'Try again',
    later: 'Later',
  },
};

const authHeaders = () => {
  const token = getLocalItem('authToken');
  return token ? { Authorization: 'Bearer ' + token } : {};
};

// ─── styles (inline only: portaled nodes escape the .mechanic-dashboard SCSS) ──
const RED = '#f42222';
const INK = '#1a1a1a';
const MUTED = '#6b7280';
const LINE = '#e4e7ec';
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", "Helvetica Neue", Arial, sans-serif';

const btnBase = {
  boxSizing: 'border-box', margin: 0, fontFamily: FONT, lineHeight: 1.3,
  textAlign: 'center', textTransform: 'none', letterSpacing: 'normal',
  outline: 'none', WebkitAppearance: 'none', appearance: 'none',
  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  userSelect: 'none', WebkitUserSelect: 'none',
};

const choice = (selected, off) => ({
  ...btnBase, display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: '100%', minHeight: '52px', padding: '6px 4px', borderRadius: '12px',
  border: '2px solid ' + (selected ? RED : LINE),
  background: selected ? '#fff1f1' : '#fff', color: selected ? RED : INK,
  fontWeight: 700, cursor: off ? 'default' : 'pointer', opacity: off ? 0.6 : 1,
});

const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.78)', zIndex: Z_INDEX,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: FONT, WebkitTextSizeAdjust: '100%',
  },
  // Column layout: header and footer (Save) stay put, only the questions scroll —
  // so Save is on screen on every phone. Sized so a 360x640 phone needs no scrolling.
  card: {
    display: 'flex', flexDirection: 'column',
    background: '#fff', borderRadius: '16px', width: 'calc(100% - 24px)', maxWidth: '420px',
    maxHeight: '94vh', overflow: 'hidden', boxSizing: 'border-box',
    boxShadow: '0 10px 40px rgba(0,0,0,0.45)', textAlign: 'left', color: INK,
  },
  header: { flex: '0 0 auto', background: RED, color: '#fff', padding: '12px 16px' },
  title: { margin: 0, padding: 0, fontFamily: FONT, fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.3, textTransform: 'none', letterSpacing: 'normal' },
  sub: { margin: '2px 0 0', padding: 0, fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1.35 },
  body: {
    flex: '1 1 auto', minHeight: 0, padding: '14px 16px 0', overflowX: 'hidden', overflowY: 'auto',
    overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
  },
  footer: { flex: '0 0 auto', padding: '12px 16px 14px', borderTop: '1px solid ' + LINE, background: '#fff' },
  section: { margin: '0 0 14px' },
  qRow: { display: 'flex', alignItems: 'center', margin: '0 0 2px' },
  qNum: (done) => ({
    flex: '0 0 26px', width: '26px', height: '26px', borderRadius: '13px', marginRight: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: done ? RED : '#fff', color: done ? '#fff' : MUTED,
    border: '2px solid ' + (done ? RED : LINE), boxSizing: 'border-box',
    fontSize: '14px', fontWeight: 800, lineHeight: 1,
  }),
  q: { margin: 0, padding: 0, fontFamily: FONT, fontSize: '18px', fontWeight: 800, color: INK, lineHeight: 1.3, textTransform: 'none', letterSpacing: 'normal' },
  hint: { margin: '0 0 8px 34px', padding: 0, fontFamily: FONT, fontSize: '13px', fontWeight: 500, color: MUTED, lineHeight: 1.4 },
  // Three across for both questions. gridGap + gap: older Android WebViews only
  // know the prefixed name.
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridGap: '8px', gap: '8px' },
  error: {
    margin: '0 0 10px', padding: '8px 12px', borderRadius: '10px', background: '#fff1f1',
    border: '1px solid #f8b4b4', color: '#b91c1c', fontSize: '15px', fontWeight: 600, lineHeight: 1.4,
  },
  save: (enabled) => ({
    ...btnBase, display: 'block', width: '100%', minHeight: '58px', padding: '12px 16px',
    borderRadius: '14px', border: '2px solid ' + (enabled ? RED : '#f0c4c4'),
    background: enabled ? RED : '#f0c4c4', color: '#fff', fontSize: '20px', fontWeight: 800,
    cursor: enabled ? 'pointer' : 'default',
  }),
  later: {
    ...btnBase, display: 'block', width: '100%', minHeight: '48px', margin: '6px 0 0',
    padding: '10px 16px', border: '2px solid transparent', background: 'transparent', color: MUTED,
    fontSize: '16px', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer',
  },
};

const validLang = (code) => (LANGS.some((l) => l.code === code) ? code : null);
const validSize = (size) => (SIZES.indexOf(size) !== -1 ? size : null);

class MechanicProfileBasicsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mounted: false,
      lang: validLang(props.currentLanguage),
      size: validSize(props.currentSize),
      saving: false,
      failed: false,
      fails: 0,
    };
    this.alive = false;
  }

  componentDidMount() {
    this.alive = true;
    this.setState({ mounted: true });
  }

  componentWillUnmount() {
    this.alive = false;
  }

  save = async () => {
    const { lang, size, saving } = this.state;
    if (!lang || !size || saving) return;
    this.setState({ saving: true, failed: false });
    try {
      // The backend takes the mechanic from the JWT, never from the body.
      const r = await axios.post(API + '/api/v2/mechanic/profile_basics',
        { language: lang, tshirt_size: size },
        { headers: authHeaders(), timeout: SAVE_TIMEOUT_MS });
      if (!r.data || r.data.status !== 'ok') throw new Error('unexpected response');
      if (!this.alive) return;
      this.setState({ saving: false });
      if (typeof this.props.onSaved === 'function') this.props.onSaved({ language: lang, tshirt_size: size });
    } catch (e) {
      if (!this.alive) return;
      this.setState((prev) => ({ saving: false, failed: true, fails: prev.fails + 1 }));
    }
  };

  later = () => {
    if (this.state.saving) return;
    if (typeof this.props.onLater === 'function') this.props.onLater();
  };

  render() {
    const { mounted, lang, size, saving, failed } = this.state;
    if (!mounted) return null;
    if (typeof document === 'undefined' || !document.body) return null;

    const s = STR[lang] || STR_DEFAULT;
    const ready = !!lang && !!size;
    const canSave = ready && !saving;

    const modal = (
      <div style={S.overlay}>
        <div style={S.card} role="dialog" aria-modal="true" aria-label={s.title}>
          <div style={S.header}>
            <h2 style={S.title}>{s.title}</h2>
            <p style={S.sub}>{s.sub}</p>
          </div>
          <div style={S.body}>
            <div style={S.section}>
              <div style={S.qRow}>
                <span style={S.qNum(!!lang)}>{lang ? '✓' : '1'}</span>
                <h3 style={S.q}>{s.langQ}</h3>
              </div>
              <p style={S.hint}>{s.langHint}</p>
              <div style={S.grid}>
                {LANGS.map((l, i) => (
                  <button
                    key={l.code}
                    type="button"
                    lang={l.code}
                    aria-pressed={lang === l.code}
                    disabled={saving}
                    onClick={() => this.setState({ lang: l.code, failed: false })}
                    style={{
                      ...choice(lang === l.code, saving), fontSize: '18px',
                      // a lone last option (English) takes the whole row
                      ...(i === LANGS.length - 1 && LANGS.length % 3 === 1 ? { gridColumn: '1 / -1' } : {}),
                    }}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={S.section}>
              <div style={S.qRow}>
                <span style={S.qNum(!!size)}>{size ? '✓' : '2'}</span>
                <h3 style={S.q}>{s.sizeQ}</h3>
              </div>
              <p style={S.hint}>{s.sizeHint}</p>
              <div style={S.grid}>
                {SIZES.map((z) => (
                  <button
                    key={z}
                    type="button"
                    aria-pressed={size === z}
                    disabled={saving}
                    onClick={() => this.setState({ size: z, failed: false })}
                    style={{ ...choice(size === z, saving), fontSize: '20px', fontWeight: 800 }}
                  >
                    {z}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={S.footer}>
            {failed && <div style={S.error} role="alert">{s.error}</div>}

            <button type="button" style={S.save(canSave)} disabled={!canSave} onClick={this.save}>
              {saving ? s.saving : (failed ? s.retry : s.save)}
            </button>

            {/* Always available — a mechanic is never blocked from the dashboard.
                Hidden only mid-save so a tap can't race the save. */}
            {!saving && (
              <button type="button" style={S.later} onClick={this.later}>{s.later}</button>
            )}
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(modal, document.body);
  }
}

export default MechanicProfileBasicsModal;
